<script>
import {paramLabel, resolveWidget} from "@/views/export-hub/exportHubApi";

export default {
  name: "ExportHubParamField",
  props: {
    param: {type: Object, required: true},
    modelValue: {default: null},
    boardsOptions: {type: Array, default: () => []},
    boardsLoading: {type: Boolean, default: false},
    regionsOptions: {type: Array, default: () => []},
    regionsLoading: {type: Boolean, default: false},
    districtsOptions: {type: Array, default: () => []},
    districtsLoading: {type: Boolean, default: false},
    quartersOptions: {type: Array, default: () => []},
    quartersLoading: {type: Boolean, default: false},
  },
  emits: ["update:modelValue", "focus-board", "focus-region", "focus-district", "focus-quarter"],
  computed: {
    widget() {
      return resolveWidget(this.param);
    },
    label() {
      return paramLabel(this.param);
    },
    enumOptions() {
      const opts = (this.param.enum || []).map((v) => ({value: v, label: v}));
      if (!this.param.required) opts.unshift({value: null, label: "Barchasi"});
      return opts;
    },
  },
  methods: {
    update(v) {
      this.$emit("update:modelValue", v);
    },
  },
};
</script>

<template>
  <div class="param-field">
    <label class="param-field__label">
      {{ label }}<span v-if="param.required" class="param-field__required">*</span>
    </label>

    <el-select
        v-if="widget === 'enum-select'"
        :model-value="modelValue"
        style="width: 100%"
        :clearable="!param.required"
        @update:model-value="update"
    >
      <el-option v-for="o in enumOptions" :key="String(o.value)" :label="o.label" :value="o.value"/>
    </el-select>

    <el-select
        v-else-if="widget === 'board-select'"
        :model-value="modelValue"
        placeholder="Mavsumni tanlang"
        filterable
        :clearable="!param.required"
        :loading="boardsLoading"
        style="width: 100%"
        @focus="$emit('focus-board')"
        @update:model-value="update"
    >
      <el-option v-for="b in boardsOptions" :key="b.value" :label="b.label" :value="b.value"/>
    </el-select>

    <el-select
        v-else-if="widget === 'region-select'"
        :model-value="modelValue"
        placeholder="Viloyatni tanlang"
        filterable
        :clearable="!param.required"
        :loading="regionsLoading"
        style="width: 100%"
        @focus="$emit('focus-region')"
        @update:model-value="update"
    >
      <el-option v-if="!param.required" label="Barchasi" :value="null"/>
      <el-option v-for="r in regionsOptions" :key="r.value" :label="r.label" :value="r.value"/>
    </el-select>

    <el-select
        v-else-if="widget === 'district-select'"
        :model-value="modelValue"
        placeholder="Tumanni tanlang"
        filterable
        :clearable="!param.required"
        :loading="districtsLoading"
        style="width: 100%"
        @focus="$emit('focus-district')"
        @update:model-value="update"
    >
      <el-option v-if="!param.required" label="Barchasi" :value="null"/>
      <el-option v-for="d in districtsOptions" :key="d.value" :label="d.label" :value="d.value"/>
    </el-select>

    <el-select
        v-else-if="widget === 'quarter-select'"
        :model-value="modelValue"
        placeholder="Mahallani qidiring"
        filterable
        :clearable="!param.required"
        :loading="quartersLoading"
        style="width: 100%"
        @focus="$emit('focus-quarter')"
        @update:model-value="update"
    >
      <el-option v-for="q in quartersOptions" :key="q.value" :label="q.label" :value="q.value"/>
    </el-select>

    <el-date-picker
        v-else-if="widget === 'year-picker'"
        :model-value="modelValue"
        type="year"
        value-format="YYYY"
        placeholder="Yilni tanlang"
        style="width: 100%"
        @update:model-value="update"
    />

    <el-date-picker
        v-else-if="widget === 'month-picker'"
        :model-value="modelValue"
        type="month"
        value-format="YYYY-MM"
        placeholder="Oyni tanlang"
        style="width: 100%"
        @update:model-value="update"
    />

    <el-date-picker
        v-else-if="widget === 'date-picker' || widget === 'date-picker-rfc3339'"
        :model-value="modelValue"
        type="date"
        value-format="YYYY-MM-DD"
        placeholder="Sanani tanlang"
        style="width: 100%"
        @update:model-value="update"
    />

    <el-input-number
        v-else-if="widget === 'number'"
        :model-value="modelValue"
        :controls="false"
        style="width: 100%"
        @update:model-value="update"
    />

    <el-checkbox
        v-else-if="widget === 'checkbox'"
        :model-value="!!modelValue"
        @update:model-value="update"
    >
      Ha
    </el-checkbox>

    <el-select
        v-else-if="widget === 'tristate-boolstr'"
        :model-value="modelValue"
        style="width: 100%"
        @update:model-value="update"
    >
      <el-option label="Barchasi" :value="null"/>
      <el-option label="Ha" value="true"/>
      <el-option label="Yo'q" value="false"/>
    </el-select>

    <el-select
        v-else-if="widget === 'tristate-active'"
        :model-value="modelValue"
        style="width: 100%"
        @update:model-value="update"
    >
      <el-option label="Barchasi" :value="null"/>
      <el-option label="Faol" value="1"/>
      <el-option label="Nofaol" value="0"/>
    </el-select>

    <el-select
        v-else-if="widget === 'tristate-checked'"
        :model-value="modelValue"
        style="width: 100%"
        @update:model-value="update"
    >
      <el-option label="Barchasi" :value="null"/>
      <el-option label="Tekshirilgan" :value="1"/>
      <el-option label="Tekshirilmagan" :value="2"/>
    </el-select>

    <el-input
        v-else
        :model-value="modelValue"
        :placeholder="label"
        @update:model-value="update"
    />

    <span v-if="param.note" class="param-field__note">{{ param.note }}</span>
  </div>
</template>

<style scoped lang="scss">
.param-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;

  &:last-child {
    margin-bottom: 0;
  }

  &__label {
    font-size: 12px;
    color: #606266;
  }

  &__required {
    color: #f56c6c;
    margin-left: 2px;
  }

  &__note {
    font-size: 11px;
    color: #999;
    line-height: 1.4;
  }
}
</style>
